import { UAParser } from 'ua-parser-js';

import { DeviceInfo, Platform, TFormData } from '#/client/models';
import { getWindowFormFactor } from '#/hooks/useResponsiveValue';
import { privacyActions } from '#/store/privacy/privacy.store';

import { StorageKeys } from '../constants';
import { LS } from '../storage';
import { falsyToNull } from '../utils';

const getCommonDeviceInfo = () => {
  const { device, browser, os } = UAParser(navigator.userAgent);

  const deviceVendor = falsyToNull(device.vendor) ?? 'UNKNOWN';
  const deviceModel = falsyToNull(device.model) ?? 'UNKNOWN';

  return {
    id: getDeviceId(),
    // Window form factor: "desktop", "tablet", "mobile", or "UNKNOWN"
    displayFormFactor: getWindowFormFactor(),
    // Window width in pixels: 1920
    displayWidth: window.innerWidth,
    // Window height in pixels: 1080
    displayHeight: window.innerHeight,
    // Browser and device: "Apple/iPhone", "Amazon/tablet", ... or "UNKNOWN/UNKNOWN"
    deviceType: `${deviceVendor}/${deviceModel}`,
    // Browser name: "Chrome", "Safari", ... or "UNKNOWN"
    browserName: falsyToNull(browser.name) ?? 'UNKNOWN',
    // Full browser version "3.1.2", "68.2541.00214", ... or "UNKNOWN"
    browserVersion: falsyToNull(browser.version) ?? 'UNKNOWN',
    // OS name: "macOS", "Windows", ... or "UNKNOWN"
    osName: falsyToNull(os.name) ?? 'UNKNOWN',
    // OS version: "11.2", "10", ... or "UNKNOWN"
    osVersion: falsyToNull(os.version) ?? 'UNKNOWN',
  };
};

// We have seen issues when desktop Safari temporarily doesn't return anything from localStorage, but only for the interrupt events.
// The current theory is that it happens when the call stack starts from a player event.
// But the player is not an iframe, so the problem is not different windows/origins having their own storage.
// And it doesn't happen in other browsers, so it might be a Safari bug.
// We resolved this exact issue by moving the event sending to microtasks, but to prevent similar issues, we also cache the device ID in memory.
let deviceIdInMemory = '';

export const getDeviceId = (): string => {
  const deviceIdStored = LS.get(StorageKeys.DEVICE_ID);
  const deviceIdCurrent = deviceIdStored ?? deviceIdInMemory ?? '';

  // we have an ID. update the cache and return.
  if (deviceIdCurrent) {
    deviceIdInMemory = deviceIdCurrent;

    return deviceIdCurrent;
  }

  // we don't have an ID. generate a new one, update the cache, save, and return.
  const deviceIdNew = crypto.randomUUID();
  deviceIdInMemory = deviceIdNew;
  LS.set(StorageKeys.DEVICE_ID, deviceIdNew);

  return deviceIdNew;
};

/**
 * Compiles the device info object that we will send with every Core API request as a header.
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/device-network-info/#supported-fields-and-values
 * - Device info: Note that there are common field/value definitions and web-specific ones listed separately.
 * - Network info: Omitted, because we don't know (as the docs say).
 * - Legal consent info: Specs are on the same page in another section.
 * @param overwriteDeviceId (GUID) Use a different deviceId than the one stored in LocalStorage. Normally you should leave it empty (to use value from LS), as it's used only in very specific scenarios
 * @returns {DeviceInfo} The device info object.
 */
export const getDeviceInfo = (overwriteDeviceId?: string): DeviceInfo => {
  const {
    id,
    deviceType,
    browserName,
    browserVersion,
    osName,
    osVersion,
    displayWidth,
    displayHeight,
    displayFormFactor,
  } = getCommonDeviceInfo();
  const consents = privacyActions.getConsents();

  return {
    // Common. Device ID.
    id: overwriteDeviceId ?? id,
    hardware: {
      // Specific. Browser and device
      manufacturer: deviceType,
      // Specific. Browser name
      model: browserName,
      // Specific. Full browser version
      version: browserVersion,
    },
    os: {
      // Specific. OS name
      name: osName,
      // Specific. OS version
      version: osVersion,
    },
    display: {
      // Common. Display width in pixels: 1920
      width: displayWidth,
      // Common. Display height in pixels: 1080
      height: displayHeight,
      // Common. Display form factor
      formFactor: displayFormFactor,
    },
    // Legal consent info
    legal: {
      tcfv2: consents?.gdpr?.tcfv2,
      customVendors: consents?.gdpr?.customVendors,
    },
  };
};

export function populateDataWithDeviceInfo(data: TFormData) {
  const deviceInfo = getDeviceInfo();

  return {
    values: { ...data },
    deviceInfo,
  };
}

export function getPlatform(): Platform {
  const {
    id,
    deviceType,
    browserName,
    osName,
    osVersion,
    displayWidth,
    displayHeight,
  } = getCommonDeviceInfo();

  return {
    type: 'Web',
    make: deviceType,
    model: browserName,
    os: osName,
    osVersion,
    resolution: `${displayWidth}x${displayHeight}`,
    uuid: id,
    browser: navigator.userAgent,
  };
}
